import React, { FC, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetCustomFieldsQuery } from "../../slices/CustomFieldsSlice";
import { CustomFieldModal, DragIcon, TrashIcon } from "./CustomFieldModals";
import { CustomField, CustomFieldEntity } from "./customFields.types";
import {
  choicesText,
  FIELD_TYPE_LABELS,
  fieldsFor,
} from "./customFields.utils";
import { useFieldList } from "./useFieldList";

const GRID =
  "grid grid-cols-[26px_1.4fr_0.7fr_1.6fr_72px] items-center gap-[14px] px-5";

const BuildingIcon = () => (
  <svg
    width={"15"}
    height={"15"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}>
    <path
      d={
        "M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"
      }
    />
  </svg>
);
const PersonIcon = () => (
  <svg
    width={"15"}
    height={"15"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}>
    <path d={"M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"} />
    <circle cx={"12"} cy={"7"} r={"4"} />
  </svg>
);

const FieldList: FC<{
  entityType: CustomFieldEntity;
  fields: CustomField[];
  loading: boolean;
  onNew: () => void;
  onEdit: (field: CustomField) => void;
}> = ({ entityType, fields, loading, onNew, onEdit }) => {
  const { ordered, dragProps, askDelete, deleteModal } = useFieldList(
    entityType,
    fields,
  );
  const isOrg = entityType === "organization";

  return (
    <section
      aria-label={isOrg ? "Organizations" : "Individuals"}
      className={isOrg ? "mb-[34px]" : ""}>
      <div className={"mb-3 flex items-center justify-between gap-4"}>
        <div className={"flex items-center gap-[9px]"}>
          <span
            className={`inline-flex h-[26px] w-[26px] items-center justify-center rounded-[7px] ${
              isOrg
                ? "bg-[#F0EDFF] text-[#6B4EFF]"
                : "bg-[#EAF1FF] text-[#3A5BC8]"
            }`}>
            {isOrg ? <BuildingIcon /> : <PersonIcon />}
          </span>
          <div className={"text-[15.5px] font-extrabold text-[#1F2433]"}>
            {isOrg ? "Organizations" : "Individuals"}
          </div>
        </div>
        <button
          type={"button"}
          onClick={onNew}
          aria-label={`New ${entityType} custom field`}
          className={
            "inline-flex h-[38px] items-center gap-[7px] rounded-[9px] bg-[#6B4EFF] px-4 text-[12.5px] font-bold text-white shadow-[0_4px_12px_rgba(107,78,255,0.3)] hover:bg-[#5B3FEF]"
          }>
          <svg
            width={"16"}
            height={"16"}
            viewBox={"0 0 24 24"}
            fill={"none"}
            stroke={"currentColor"}
            strokeWidth={"2.4"}
            strokeLinecap={"round"}>
            <path d={"M12 5v14M5 12h14"} />
          </svg>
          New custom field
        </button>
      </div>
      <div
        className={
          "overflow-hidden rounded-[14px] border border-[#E8E8EC] bg-white"
        }>
        <div
          className={`${GRID} border-b border-[#ECEEF2] bg-[#FCFCFD] py-[13px]`}>
          <div />
          {["Field name", "Type", "Choices"].map((title) => (
            <div
              key={title}
              className={
                "text-[12px] font-bold uppercase tracking-[0.4px] text-[#9097A8]"
              }>
              {title}
            </div>
          ))}
          <div />
        </div>
        {ordered.map((field) => {
          const drag = dragProps(field);
          return (
            <div
              key={field.id}
              data-testid={"custom-field-row"}
              onDragEnter={drag.row.onDragEnter}
              onDragOver={drag.row.onDragOver}
              onDrop={drag.row.onDrop}
              className={`${GRID} border-b border-[#F0F1F4] py-[15px] transition-[opacity,box-shadow] last:border-b-0 ${
                drag.row.dragging
                  ? "bg-[#F6F4FF] opacity-40 shadow-[0_8px_20px_rgba(20,22,34,0.16)]"
                  : "bg-white"
              }`}>
              <span
                {...drag.handle}
                className={
                  "inline-flex h-8 w-[22px] cursor-grab items-center justify-center text-[#B4BAC7] hover:text-[#6B4EFF]"
                }>
                <DragIcon />
              </span>
              <div
                className={"break-words text-[14px] font-bold text-[#1F2433]"}>
                {field.label}
              </div>
              <div>
                <span
                  className={
                    "rounded-md bg-[#F1EEFF] px-[9px] py-[3px] text-[10.5px] font-bold uppercase tracking-[0.3px] text-[#6B4EFF]"
                  }>
                  {FIELD_TYPE_LABELS[field.fieldType]}
                </span>
              </div>
              <div className={"break-words text-[13px] text-[#6B7185]"}>
                {choicesText(field)}
              </div>
              <div className={"flex justify-end gap-0.5"}>
                <button
                  type={"button"}
                  title={"Edit"}
                  aria-label={`Edit ${field.label}`}
                  onClick={() => onEdit(field)}
                  className={
                    "inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7185] hover:bg-[#F1F2F5] hover:text-[#6B4EFF]"
                  }>
                  <svg
                    width={"15"}
                    height={"15"}
                    viewBox={"0 0 24 24"}
                    fill={"none"}
                    stroke={"currentColor"}
                    strokeWidth={"1.9"}
                    strokeLinecap={"round"}
                    strokeLinejoin={"round"}>
                    <path d={"M12 20h9"} />
                    <path d={"M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"} />
                  </svg>
                </button>
                <button
                  type={"button"}
                  title={"Delete"}
                  aria-label={`Delete ${field.label}`}
                  onClick={() => askDelete(field)}
                  className={
                    "inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9097A8] hover:bg-[#FEF2F2] hover:text-[#E5484D]"
                  }>
                  <TrashIcon />
                </button>
              </div>
            </div>
          );
        })}
        {!loading && ordered.length === 0 && (
          <div className={"px-5 py-[34px] text-center"}>
            <div
              className={"mb-[5px] text-[14px] font-semibold text-[#3A4256]"}>
              No {isOrg ? "organization" : "individual"} fields yet
            </div>
            <div className={"text-[13px] text-[#9097A8]"}>
              Create a custom field to capture extra info on{" "}
              {isOrg ? "organizations" : "individuals"}.
            </div>
          </div>
        )}
        {loading && (
          <div
            className={"px-5 py-[34px] text-center text-[13px] text-[#9097A8]"}>
            Loading…
          </div>
        )}
      </div>
      {deleteModal}
    </section>
  );
};

/** Settings > Custom Fields. The Settings page already checks access. */
export const CustomFieldsSettings: FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const {
    data: fields,
    isLoading,
    isError,
  } = useGetCustomFieldsQuery({ orgId }, { skip: !orgId });
  const [modal, setModal] = useState<{
    entityType: CustomFieldEntity;
    field?: CustomField;
  } | null>(null);

  return (
    <div className={"pb-10"}>
      <div
        className={
          "mb-1.5 text-[22px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
        }>
        Custom Fields
      </div>
      <div className={"mb-6 max-w-[640px] text-[13.5px] text-[#6B7185]"}>
        Custom fields created here become available on every contact of the
        matching type. Enter a value per contact from that contact’s Edit
        screen.
      </div>
      {isError && (
        <div
          className={
            "mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-semibold text-error"
          }>
          Custom fields couldn’t be loaded. Please refresh the page.
        </div>
      )}
      {(["organization", "individual"] as CustomFieldEntity[]).map(
        (entityType) => (
          <FieldList
            key={entityType}
            entityType={entityType}
            fields={fieldsFor(fields, entityType)}
            loading={isLoading}
            onNew={() => setModal({ entityType })}
            onEdit={(field) => setModal({ entityType, field })}
          />
        ),
      )}
      {modal && (
        <CustomFieldModal
          entityType={modal.entityType}
          field={modal.field}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};
